import { Refresh } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { practicePaymentApi } from "apis";
import { BillingHistoryTable } from "components/features/practice";
import DashboardLayout from "components/layouts/DashboardLayout";
import dayjs from "dayjs";
import { useSubscription } from "hooks/useSubscription";
import React from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

// Enums for better type safety and consistency
enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  EXPIRED = "expired",
}

// Default values
const DEFAULT_VALUES = {
  PLAN_NAME: "Professional Plan",
  PRICE: "$1,100/month",
  RENEWAL_DATE: "Oct 20, 2025",
} as const;

// Subscription Section Component
interface SubscriptionSectionProps {
  subscription: {
    planName?: string;
    price?: string;
    renewalDate?: string;
  } | null;
  onChangePlan?: () => void;
  onCancelSubscription?: () => void;
  onUpdatePaymentMethod?: () => void;
}

const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
  subscription,
  onChangePlan,
  onCancelSubscription,
  onUpdatePaymentMethod,
}) => {
  const { colors } = useCelleraTheme();

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format("MMM DD, YYYY");
    } catch {
      return dateString;
    }
  };

  // Define subscription details with dynamic rendering
  const subscriptionDetails = [
    {
      label: "Plan",
      value: subscription?.planName || DEFAULT_VALUES.PLAN_NAME,
    },
    {
      label: "Price",
      value: subscription?.price || DEFAULT_VALUES.PRICE,
    },
    {
      label: "Renewal",
      value: subscription?.renewalDate
        ? formatDate(subscription.renewalDate)
        : DEFAULT_VALUES.RENEWAL_DATE,
    },
    {
      label: "Status",
      value: (
        <Chip
          label={SubscriptionStatus.ACTIVE.toUpperCase()}
          size="small"
          sx={{
            backgroundColor: colors.success[100],
            color: colors.success[700],
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      ),
    },
  ];

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: colors.text.primary,
          mb: { xs: 1.5, sm: 2 },
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
        }}
      >
        Practice Subscription
      </Typography>
      <Card sx={{ borderRadius: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${colors.border.light}`,
                pb: 1,
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Current Plan
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 3, md: 0 },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {subscriptionDetails.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: { xs: "auto", sm: 80 } }}
                    >
                      {detail.label}:
                    </Typography>
                    {typeof detail.value === "string" ? (
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {detail.value}
                      </Typography>
                    ) : (
                      detail.value
                    )}
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: { xs: "100%", md: "auto" },
                  mt: { xs: 0, md: 3.5 },
                  maxWidth: { xs: "none", md: 200 },
                }}
              >
                <Button
                  variant="contained"
                  onClick={onChangePlan}
                  sx={{
                    backgroundColor: colors.primary.main,
                    color: "white",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    width: "100%",
                    "&:hover": {
                      backgroundColor: colors.primary.dark,
                    },
                  }}
                >
                  Change Plan
                </Button>
                <Button
                  variant="outlined"
                  onClick={onUpdatePaymentMethod}
                  sx={{
                    borderColor: colors.border.light,
                    color: colors.text.secondary,
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    width: "100%",
                  }}
                >
                  Update Payment
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onCancelSubscription}
                  sx={{
                    borderColor: colors.error[500],
                    color: colors.error[500],
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    width: "100%",
                  }}
                >
                  Cancel Subscription
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const PaymentManagementPage: React.FC = () => {
  const { colors } = useCelleraTheme();
  const {
    subscription,
    transactions,
    totalCount,
    loading,
    page,
    pageSize,
    setPageSize,
    refetch,
    viewInvoice,
  } = useSubscription();

  const handleRefresh = () => {
    refetch();
  };

  // Enum for subscription actions
  enum SubscriptionAction {
    CHANGE_PLAN = "change_plan",
    CANCEL_SUBSCRIPTION = "cancel_subscription",
    UPDATE_PAYMENT_METHOD = "update_payment_method",
  }

  // New method to handle different subscription actions
  const handleSubscriptionAction = async (action: SubscriptionAction) => {
    try {
      let portalUrl;
      switch (action) {
        case SubscriptionAction.CHANGE_PLAN:
          portalUrl = await practicePaymentApi.createUpdateSubscriptionUrl();
          break;
        case SubscriptionAction.CANCEL_SUBSCRIPTION:
          portalUrl = await practicePaymentApi.createCancelSubscriptionUrl();
          break;
        case SubscriptionAction.UPDATE_PAYMENT_METHOD:
          portalUrl = await practicePaymentApi.createUpdatePaymentMethodUrl();
          break;
      }

      // Redirect user to Stripe customer portal
      if (portalUrl?.url) {
        window.location.href = portalUrl.url;
      }
    } catch (error) {
      console.error(`Error getting ${action} URL:`, error);
      // You might want to add error handling, like showing a toast notification
    }
  };

  // Update existing method to use the new handler
  const handleChangePlan = () => {
    handleSubscriptionAction(SubscriptionAction.CHANGE_PLAN);
  };

  // Add methods for cancel subscription and update payment method
  const handleCancelSubscription = () => {
    handleSubscriptionAction(SubscriptionAction.CANCEL_SUBSCRIPTION);
  };

  const handleUpdatePaymentMethod = () => {
    handleSubscriptionAction(SubscriptionAction.UPDATE_PAYMENT_METHOD);
  };

  return (
    <DashboardLayout
      title="Payment Management"
      headerActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 3 },
          px: { xs: 0, sm: 0 },
        }}
      >
        <SubscriptionSection
          subscription={subscription}
          onChangePlan={handleChangePlan}
          onCancelSubscription={handleCancelSubscription}
          onUpdatePaymentMethod={handleUpdatePaymentMethod}
        />

        {/* Billing History Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            Billing History
          </Typography>

          <BillingHistoryTable
            transactions={transactions}
            loading={loading}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageSizeChange={setPageSize}
            onViewInvoice={viewInvoice}
          />
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default PaymentManagementPage;
